import { useEffect, useRef, useState, type ReactNode } from "react";
import { Button } from "./components";

/** The browser top layer also blocks host UI outside an embedded editor. */
export function ImportProgress({
  children,
  onCancel,
  cancelLabel = "Cancel import",
}: {
  children: ReactNode;
  onCancel: () => void;
  cancelLabel?: string;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [confirming, setConfirming] = useState(false);
  useEffect(() => {
    const element = dialog.current!;
    element.showModal();
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };
    const leave = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("keydown", escape, true);
    window.addEventListener("beforeunload", leave);
    return () => {
      window.removeEventListener("keydown", escape, true);
      window.removeEventListener("beforeunload", leave);
      element.close();
    };
  }, []);
  return (
    <dialog
      ref={dialog}
      aria-label={
        confirming ? "Confirm cancellation" : "Upload and import progress"
      }
      role={confirming ? "alertdialog" : "dialog"}
      aria-modal="true"
      onCancel={(event) => event.preventDefault()}
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100dvh",
        maxWidth: "none",
        maxHeight: "none",
        boxSizing: "border-box",
        margin: 0,
        border: 0,
        padding: "clamp(24px, 6vw, 80px)",
        background: "var(--ps-neutral-1)",
        color: "var(--ps-neutral-12)",
        overflow: "auto",
      }}
    >
      <div style={{ maxWidth: 760, margin: "auto" }}>
        {confirming ? (
          <>
            <h2>Are you sure you want to cancel?</h2>
            <p>
              The current upload or import will stop. Projects already imported
              will be kept.
            </p>
            <div className="button-row">
              <Button onClick={() => setConfirming(false)}>
                Keep importing
              </Button>
              <Button onClick={onCancel}>Yes, cancel</Button>
            </div>
          </>
        ) : (
          <>
            <h2>Upload / import in progress</h2>
            <p>
              Please keep this page open. You can cancel below if you need to
              stop.
            </p>
            <div role="status" aria-live="polite">
              {children}
            </div>
            <Button onClick={() => setConfirming(true)}>{cancelLabel}</Button>
          </>
        )}
      </div>
    </dialog>
  );
}
